/** Serialize a Spiffy checkout URL so spaces travel as %20, never "+".
 *
 *  URL.searchParams writes a space as "+" (form encoding), but Spiffy's embed
 *  decodes its prefill params with plain percent-decoding, so "+" stays a
 *  literal plus. A buyer named "Mary Ann" arrived as "Mary+Ann" on the order,
 *  in the webhook, on our thanks and survey pages and in every email
 *  (Summit AU test order 2548724, 2026-10-14, mct_09f03514-f98).
 *
 *  A literal "+" in a value (plus-addressed emails) is already written as
 *  %2B by searchParams, so every bare "+" in the query is a space. */
export function spiffyUrlString(u: URL): string {
  return u.search ? u.toString().replace(u.search, u.search.replace(/\+/g, '%20')) : u.toString();
}

/** Merge the gate's checkout params (the member's code) into a Spiffy URL.
 *  A gate code replaces any `code` passed through from the page address. */
export function withGateParams(spiffyUrl: string, params: Record<string, string>): string {
  if (!spiffyUrl || Object.keys(params).length === 0) return spiffyUrl;
  try {
    const u = new URL(spiffyUrl);
    for (const [k, v] of Object.entries(params)) u.searchParams.set(k, v);
    // Re-serialising through searchParams turns every %20 back into "+", which
    // Spiffy reads literally ("Mary+Ann"). Keep spaces as %20.
    return spiffyUrlString(u);
  } catch {
    return spiffyUrl;
  }
}

/** Spiffy's own embed script re-encodes every query value and turns a space
 *  into a literal "+" (proven live 14 Oct: we handed it name_first=Zed%20Test
 *  and its iframe received Zed%2BTest). No encoding of a space survives it, so
 *  a name with a space is never put on a Spiffy URL: the buyer types it (or
 *  identify() fills it). Single-word names prefill as before. */
export function spiffyPrefillName(v: string | null | undefined): string | null {
  const t = (v ?? '').trim();
  return t && !/\s/.test(t) ? t : null;
}

/** Spiffy also copies the embedding page's own query params into the form, so
 *  a spaced first_name/last_name on OUR address arrives mangled too. Drop any
 *  name param that holds a space from the page address before Spiffy loads. */
export const SPIFFY_NAME_PARAMS = ['first_name', 'last_name', 'name_first', 'name_last', 'fn', 'ln'] as const;
export function withoutSpacedNameParams(href: string): string | null {
  try {
    const u = new URL(href);
    let changed = false;
    for (const k of SPIFFY_NAME_PARAMS) {
      const v = u.searchParams.get(k);
      if (v !== null && /\s/.test(v.trim())) { u.searchParams.delete(k); changed = true; }
    }
    return changed ? u.toString() : null;
  } catch {
    return null;
  }
}

/** Page params the checkout page maps itself — prefill goes onto Spiffy under
 *  Spiffy's own names, and `cur` picks which checkout loads. */
const CHECKOUT_HANDLED_PARAMS = new Set<string>(['email', 'cur', ...SPIFFY_NAME_PARAMS]);

/** Carry every other param on our page address onto the Spiffy URL unchanged:
 *  `code` pre-applies a promo (booth offer links, mct_23b6b0ba-360), UTMs and
 *  click ids keep attribution, `ref` and the rest ride along. The link's value
 *  wins over one baked into the checkout URL. Same rule as /summit, which keeps
 *  the whole query string on its redirect (geo-routes.ts). */
export function withPagePassthrough(u: URL, params: URLSearchParams): void {
  for (const [k, v] of params) {
    if (!v || CHECKOUT_HANDLED_PARAMS.has(k)) continue;
    u.searchParams.set(k, v);
  }
}
